import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { mockData } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-primary-blue text-white">
      <div className="container mx-auto px-4 pt-12 pb-24 md:pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Logo et description */}
          <div className="lg:col-span-1">
            <div className="mb-4">
              <div className="inline-block bg-white rounded-xl p-3">
                <img 
                  src="/mmd-logo.png" 
                  alt="MMD CONSEIL Logo" 
                  className="h-12 w-auto object-contain"
                />
              </div>
            </div>
            <p className="text-blue-100 text-sm leading-relaxed">
              {mockData.cabinet.description}
            </p>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Navigation</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-blue-100 hover:text-white transition-colors duration-200">
                  Accueil
                </Link>
              </li>
              <li>
                <Link to="/a-propos" className="text-blue-100 hover:text-white transition-colors duration-200">
                  À propos
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-blue-100 hover:text-white transition-colors duration-200">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-blue-100 hover:text-white transition-colors duration-200">
                  Blog
                </Link>
              </li>
              <li>
                <Link to="/actualites" className="text-blue-100 hover:text-white transition-colors duration-200">
                  Actualités
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-blue-100 hover:text-white transition-colors duration-200">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Services & Accès */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Nos Services</h3>
            <ul className="space-y-2">
              <li className="text-blue-100 text-sm">Expertise Comptable</li>
              <li className="text-blue-100 text-sm">Fiscalité</li>
              <li className="text-blue-100 text-sm">Social & Paie</li>
              <li className="text-blue-100 text-sm">Conseil & Accompagnement</li>
            </ul>
            <div className="mt-4 space-y-2">
              <a 
                href="https://app.pennylane.com" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 text-blue-200 hover:text-white transition-colors duration-200 text-sm"
              >
                <img src="/pennylane-icon.png" alt="Pennylane" className="w-4 h-4" />
                <span>Espace client Pennylane</span>
              </a>
              <br />
              <a 
                href="https://www.linkedin.com/company/mmd-conseil" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 bg-[#0077B5] hover:bg-[#005885] text-white px-4 py-2 rounded-lg transition-colors duration-200 text-sm font-medium"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                <span>Suivez-nous sur LinkedIn</span>
              </a>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-semibold text-lg mb-4">Contact</h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-blue-200 mt-0.5 flex-shrink-0" />
                <span className="text-blue-100 text-sm">{mockData.cabinet.adresse}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-blue-200" />
                <span className="text-blue-100 text-sm">{mockData.cabinet.telephone}</span>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="w-5 h-5 text-blue-200" />
                <span className="text-blue-100 text-sm">{mockData.cabinet.email}</span>
              </div>
              <div className="flex items-start space-x-3">
                <Clock className="w-5 h-5 text-blue-200 mt-0.5" />
                <div className="text-blue-100 text-sm">
                  <p>Lun - Ven: 9h00 - 17h00</p>
                  <p>Sam: Sur rendez-vous</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-blue-800 mt-12 pt-6">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-blue-200">
            <p>© {currentYear} MMD CONSEIL. Tous droits réservés.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <Link to="/mentions-legales" data-testid="footer-mentions-legales" className="hover:text-white transition-colors duration-200">
                Mentions légales
              </Link>
              <Link to="/politique-de-confidentialite" data-testid="footer-politique-confidentialite" className="hover:text-white transition-colors duration-200">
                Politique de confidentialité
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;